import { c as _c } from "react/compiler-runtime";
import { useQuery } from '@apollo/client/react';
import { Box, Button, Center, HStack, Heading, SimpleGrid, Spinner, Text } from '@chakra-ui/react';
import { useEducationContext } from '@src/components/sco/Education/EducationContext';
import { PlaybookPhaseCard } from '@src/components/sco/Education/PlaybookDetail/PlaybookPhaseCard';
import { PlaybookCard } from '@src/components/sco/Education/PlaybookList/PlaybookCard';
import { UserAccessGate } from '@src/components/sco/UserAccessGate';
import { ArrowForwardIcon } from '@src/components/sco/icons';
import { useBreakpointContext } from '@src/components/sco/utilities/BreakpointProvider';
import { NextLink } from '@src/components/sco/utilities/NextLink';
import { PLAYBOOK } from '@src/gql/queries/Playbook';
import { Query, ScoPlaybook } from '@src/graphql-types';
import { useUniversalEffect } from '@src/hooks/useUniversalEffect';
import { usePathname } from 'next/navigation';
import { useQueryState } from 'nuqs';
import React from 'react';
type PlaybookDetailProps = {
  playbookSlug: string;
};
export function renderPhases(playbookSlug: string, sections = []) {
  return sections.map(section => <PlaybookPhaseCard key={section.id} badge={section.badge} href={`/playbooks/${playbookSlug}/${section.slug}`} teaser={section.teaser} title={section.title} />);
}
export function renderLearningPoints(learningPoints = []) {
  return learningPoints?.map((lp, index) => <Box key={`${index}-${lp}`} alignItems={'center'} bgColor={'colorPalette.subtle'} borderRadius={'xl'} display={'flex'} justifyContent={'center'} padding={10} width={'100%'}>
      <Text color={'colorPalette.fg'} fontSize={'md'} fontWeight={'medium'} textAlign={'center'}>
        {lp}
      </Text>
    </Box>);
}
export const PlaybookDetail = props => {
  const $ = _c(22);
  const {
    playbookSlug
  } = props;
  const educationContext = useEducationContext();
  const {
    getBreakpointValue
  } = useBreakpointContext();
  const pathname = usePathname();
  const [, setPrompt] = useQueryState("prompt");
  let t0;
  if ($[0] !== playbookSlug) {
    t0 = {
      variables: {
        playbookSlug
      }
    };
    $[0] = playbookSlug;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const {
    data,
    error,
    loading
  } = useQuery(PLAYBOOK, t0);
  let t1;
  if ($[2] !== data?.scoPlaybook?.slug || $[3] !== data?.scoPlaybook?.title || $[4] !== educationContext) {
    t1 = () => {
      educationContext.setActiveSection("playbooks.single");
      educationContext.setBreadcrumbs([{
        title: "Playbooks",
        href: "/playbooks"
      }, {
        title: data?.scoPlaybook?.title || "...",
        href: `/playbooks/${data?.scoPlaybook?.slug}`
      }]);
    };
    $[2] = data?.scoPlaybook?.slug;
    $[3] = data?.scoPlaybook?.title;
    $[4] = educationContext;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const t2 = data?.scoPlaybook?.id;
  let t3;
  if ($[6] !== t2) {
    t3 = [t2];
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  useUniversalEffect(t1, t3);
  const playbook = data?.scoPlaybook;
  let t4;
  if ($[8] !== getBreakpointValue || $[9] !== pathname || $[10] !== playbook || $[11] !== setPrompt) {
    t4 = playbook && <><PlaybookCard bgColor={`${playbook.topic?.colorHexCode}`} buttonLabel="Start Playbook" direction={getBreakpointValue(["column", "column", "column", "row-reverse"])} illustration={playbook.illustration} summary={playbook.teaser} tag="Tag" tagColor={playbook.topic?.colorHexCode} targetUrl={`/playbooks/${playbook.slug}/${playbook.sections[0]?.slug}`} title={playbook.title} /><Box paddingY="1em"><UserAccessGate ctaVariant="inline-card" require={{
          isRegistered: true
        }} ctaProps={{
          title: "Register to unlock this playbook.",
          buttonProps: {
            href: `${pathname}?prompt=register`,
            onClick: ev => {
              ev.preventDefault();
              setPrompt("register");
            },
            title: "Create a Free Account"
          }
        }} /></Box><Heading paddingY="1em" whiteSpace="nowrap">Phases</Heading><SimpleGrid columns={[1, 1, 2, 3]} gap="1em">{renderPhases(playbook.slug, playbook.sections)}</SimpleGrid><Heading paddingBottom="1em" paddingTop="2em" whiteSpace="nowrap">What you'll learn</Heading><SimpleGrid columns={[1, 1, 2, 3]} gap="1em">{renderLearningPoints(playbook?.learningPoints)}</SimpleGrid><HStack align="center" bgColor={`${playbook.topic?.colorHexCode}50`} borderRadius={12} gap={10} justify="center" marginY={10} paddingX={10} paddingY={10} width="full"><Heading fontSize={24}>Ready to get started?</Heading><Button asChild={true} _hover={{
          bgColor: playbook.topic?.colorHexCode,
          opacity: 0.85
        }} bgColor={playbook.topic?.colorHexCode}><NextLink href={`/playbooks/${playbook.slug}/${playbook.sections[0]?.slug}`}>Start Reading<ArrowForwardIcon /></NextLink></Button></HStack></>;
    $[8] = getBreakpointValue;
    $[9] = pathname;
    $[10] = playbook;
    $[11] = setPrompt;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== error) {
    t5 = error && <Center padding="24px">{"Error: "}{error.toString()}</Center>;
    $[13] = error;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== loading || $[16] !== playbook) {
    t6 = (!playbook || loading) && <Center padding="24px"><Spinner /></Center>;
    $[15] = loading;
    $[16] = playbook;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== t4 || $[19] !== t5 || $[20] !== t6) {
    t7 = <>{t4}{t5}{t6}</>;
    $[18] = t4;
    $[19] = t5;
    $[20] = t6;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  return t7;
};